import React from 'react';
import Head from 'next/head';
import { getRuntimeConfig } from '../../lib/runtimeConfig';
import { useRouter } from 'next/router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useI18nContext } from '@procore/core-react';
import Header from '../../components/Layout/Header';
import { iterableBusinessTypes } from '../../data/filters/business-type';
import { iterableMarketSectors } from '../../data/filters/market-sectors';
import { alphabeticalProvidedServices } from '../../data/filters/provided-services';
import routes from '../../helpers/routes';
import Footer from '../../components/Layout/Footer';
import { CANADA, DEFAULT_COUNTRY } from '../../data/location/countries';
import NoStyleLink from '../../components/ButtonsLinks/NoStyleLink';
import { getStateList } from '../../data/location/states';
import { useStyles } from './index.styles';

const publicRuntimeConfig = getRuntimeConfig();

export async function getServerSideProps() {
  const results = [];
  return {
    props: {
      businesses: results,
    },
  };
}

const Sitemap = () => {
  const I18n = useI18nContext();
  const { locale } = useRouter();
  const classes = useStyles();

  const country =
    locale === 'en-CA' || locale === 'fr-CA' ? CANADA : DEFAULT_COUNTRY;

  return (
    <main style={{ height: '100% ' }}>
      <Head>
        <title>{I18n.t('pages.sitemap.title')}</title>
        <meta
          name="description"
          content="Procore Construction Network sitemap"
        />
      </Head>
      <Container className={classes.headerAndContent}>
        <Header />
        <Container className={classes.sitemapContainer}>
          <Typography component="h1" variant="h1">
            {I18n.t('pages.sitemap.pcn_sitemap')}
          </Typography>
          <Box className={classes.accordionContainer}>
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography component="h2" variant="h2">
                  {I18n.t('pages.sitemap.company_types')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box className={classes.accordionContent}>
                  {iterableBusinessTypes.map((b) => (
                    <NoStyleLink
                      key={b.name}
                      className={classes.accordionLink}
                      href={routes.businessTypes(b.urlKey)}
                    >
                      {b.name}
                    </NoStyleLink>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography component="h2" variant="h2">
                  {I18n.t('pages.sitemap.market_sectors')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box className={classes.accordionContent}>
                  {iterableMarketSectors.map((b) => (
                    <NoStyleLink
                      key={b.name}
                      className={classes.accordionLink}
                      href={routes.marketSectors(b.urlKey)}
                    >
                      {b.name}
                    </NoStyleLink>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography component="h2" variant="h2">
                  {I18n.t('pages.sitemap.trades')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box className={classes.accordionContent}>
                  {alphabeticalProvidedServices.map((b) => (
                    <NoStyleLink
                      key={b.name}
                      className={classes.accordionLink}
                      href={routes.trades(b)}
                    >
                      {b.name}
                    </NoStyleLink>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion className={classes.accordion}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                data-test-id="provinces-label-container"
              >
                <Typography
                  component="h2"
                  variant="h2"
                  data-test-id="provinces-label"
                >
                  {locale === 'en-US'
                    ? I18n.t('pages.sitemap.contractors_by_state')
                    : I18n.t('pages.sitemap.contractors_by_province')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box className={classes.accordionContent}>
                  {getStateList(country).map((b) => (
                    <NoStyleLink
                      key={b.name}
                      className={classes.accordionLink}
                      href={routes.location(country.alpha2, b.abbreviation)}
                      data-test-id="province"
                    >
                      {I18n.t('pages.sitemap.contractors_in', { name: b.name })}
                    </NoStyleLink>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
            {country.alpha2 !== 'CA' && (
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  data-test-id="provinces-label-container"
                >
                  <Typography
                    component="h2"
                    variant="h2"
                    data-test-id="provinces-label"
                  >
                    {locale === 'en-US'
                      ? I18n.t('pages.sitemap.projects_by_state')
                      : I18n.t('pages.sitemap.projects_by_province')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className={classes.accordionContent}>
                    {getStateList(country).map((b) => (
                      <NoStyleLink
                        key={b.name}
                        className={classes.accordionLink}
                        href={routes.location(
                          country.alpha2,
                          b.abbreviation,
                          null,
                          null,
                          null,
                          null,
                          true
                        )}
                        data-test-id="province"
                      >
                        {I18n.t('pages.sitemap.open_projects_in', {
                          name: b.name,
                        })}
                      </NoStyleLink>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography component="h2" variant="h2">
                  {I18n.t('pages.sitemap.signup')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box className={classes.accordionContent}>
                  <NoStyleLink
                    className={classes.accordionLink}
                    href={`${publicRuntimeConfig.signupLink}`}
                  >
                    {I18n.t('pages.sitemap.signup')}
                  </NoStyleLink>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Container>
      </Container>
      <Footer showDiscover={false} />
    </main>
  );
};

export default Sitemap;
